const ITEM_REFERENCE_PREFIX = '!item';
const BOM_REFERENCE_PREFIX = '!bom';
const GUIDE_REFERENCE_PREFIX = '!guide';
const REFERENCE_PREFIXES = [ITEM_REFERENCE_PREFIX, BOM_REFERENCE_PREFIX, GUIDE_REFERENCE_PREFIX];
const MARKDOWN_REFERENCE_TEXT_REGEX = /!item\[([^\]\r\n]+)\]\(([^)\r\n]+)\)(?:\(([^)\r\n]*)\))?|!bom\[([^\]\r\n]+)\](?:\(([^)\r\n]*)\))?|!guide\[([^\]\r\n]+)\](?:\(([^)\r\n]*)\))?/g;
const MARKDOWN_REFERENCE_HREF_PREFIX = '/_mdref/';
const OPTIONAL_NAME_PREFIX_REGEX = /^\(([^)\r\n]*)\)/;
const BOM_FILTER_TYPES = new Set(['category', 'module', 'version']);

function sanitizeReferencePart(value) {
  return (value || '').toString().trim();
}

function safeDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value || '');
  } catch (e) {
    return '';
  }
}

function normalizeReferenceKey(value) {
  return sanitizeReferencePart(value).toLowerCase();
}

function hasMarkdownReferencePrefix(value) {
  const text = (value || '').toString();
  return REFERENCE_PREFIXES.some((prefix) => text.includes(`${prefix}[`));
}

function findTrailingReferencePrefix(value) {
  const text = (value || '').toString();
  let bestIndex = -1;
  let bestPrefix = '';

  REFERENCE_PREFIXES.forEach((prefix) => {
    const index = text.lastIndexOf(prefix);
    if (index > bestIndex) {
      bestIndex = index;
      bestPrefix = prefix;
    }
  });

  return {
    index: bestIndex,
    prefix: bestPrefix
  };
}

function extractTextFromLinkNode(linkNode) {
  if (!linkNode || !Array.isArray(linkNode.children)) return '';
  return linkNode.children
    .filter((child) => child?.type === 'text' && typeof child.value === 'string')
    .map((child) => child.value)
    .join('');
}

function decodeMarkdownLinkUrlValue(linkUrl) {
  const raw = sanitizeReferencePart(linkUrl);
  if (!raw) return '';

  try {
    const parsed = new URL(raw, 'https://stepwisedocs.local');
    const decodedPath = safeDecodeURIComponent((parsed.pathname || '').replace(/^\/+/, ''));
    return sanitizeReferencePart(decodedPath);
  } catch (e) {
    return sanitizeReferencePart(raw);
  }
}

function normalizeReferenceType(value) {
  const normalized = sanitizeReferencePart(value).toLowerCase();
  if (normalized === 'item') return 'item';
  if (normalized === 'bom') return 'bom';
  if (normalized === 'guide') return 'guide';
  return '';
}

function normalizeBomFilterType(value) {
  const normalized = sanitizeReferencePart(value).toLowerCase();
  return BOM_FILTER_TYPES.has(normalized) ? normalized : '';
}

function parseBomReferenceTarget(value) {
  const raw = sanitizeReferencePart(value);
  if (!raw) {
    return {
      slug: '',
      filterType: '',
      filterValue: ''
    };
  }

  const parts = raw.split(',');
  const slug = sanitizeReferencePart(parts.shift());
  let filterType = '';
  let filterValue = '';

  parts.forEach((part) => {
    if (filterType) return;
    const separatorIndex = part.indexOf('=');
    if (separatorIndex < 0) return;
    const candidateType = normalizeBomFilterType(part.slice(0, separatorIndex));
    const candidateValue = sanitizeReferencePart(part.slice(separatorIndex + 1));
    if (!candidateType || !candidateValue) return;
    filterType = candidateType;
    filterValue = candidateValue;
  });

  return {
    slug,
    filterType,
    filterValue
  };
}

function buildMarkdownReference(matchGroups) {
  const [
    itemBomSlugRaw,
    itemIdentifierRaw,
    itemOptionalNameRaw,
    bomSlugRaw,
    bomOptionalNameRaw,
    guideSlugRaw,
    guideOptionalNameRaw
  ] = matchGroups;

  const itemBomSlug = sanitizeReferencePart(itemBomSlugRaw);
  if (itemBomSlug) {
    const identifier = sanitizeReferencePart(itemIdentifierRaw).toLowerCase();
    if (!identifier) return null;
    return {
      type: 'item',
      bomSlug: itemBomSlug,
      identifier,
      optionalName: sanitizeReferencePart(itemOptionalNameRaw)
    };
  }

  const bomTarget = parseBomReferenceTarget(bomSlugRaw);
  if (bomTarget.slug) {
    return {
      type: 'bom',
      slug: bomTarget.slug,
      filterType: bomTarget.filterType,
      filterValue: bomTarget.filterValue,
      optionalName: sanitizeReferencePart(bomOptionalNameRaw)
    };
  }

  const guideSlug = sanitizeReferencePart(guideSlugRaw);
  if (guideSlug) {
    return {
      type: 'guide',
      slug: guideSlug,
      optionalName: sanitizeReferencePart(guideOptionalNameRaw)
    };
  }

  return null;
}

function getReferenceFallbackLabel(reference) {
  if (reference?.type === 'item') {
    return reference.optionalName || reference.identifier || 'unknown item';
  }
  if (reference?.type === 'bom') {
    return reference.optionalName || reference.slug || 'unknown BOM';
  }
  if (reference?.type === 'guide') {
    return reference.optionalName || reference.slug || 'unknown guide';
  }
  return 'unknown reference';
}

function createMarkdownReferenceLinkNode(reference) {
  const href = buildMarkdownReferenceHref(reference);
  const fallbackLabel = getReferenceFallbackLabel(reference);
  return {
    type: 'link',
    url: href,
    children: [{ type: 'text', value: fallbackLabel }]
  };
}

function buildMarkdownReferenceFromSplitNodes(prefix, linkNode, trailingNode) {
  const normalizedPrefix = normalizeReferenceType(prefix.replace('!', ''));
  if (!normalizedPrefix) return null;

  const slugFromLinkText = sanitizeReferencePart(extractTextFromLinkNode(linkNode));
  if (!slugFromLinkText) return null;

  if (normalizedPrefix === 'item') {
    const identifier = decodeMarkdownLinkUrlValue(linkNode?.url).toLowerCase();
    if (!identifier) return null;

    let optionalName = '';
    let removeCount = 2;
    let remainderText = '';

    if (trailingNode?.type === 'text') {
      const trailingValue = (trailingNode.value || '').toString();
      const optionalMatch = trailingValue.match(OPTIONAL_NAME_PREFIX_REGEX);
      if (optionalMatch) {
        optionalName = sanitizeReferencePart(optionalMatch[1]);
        remainderText = trailingValue.slice(optionalMatch[0].length);
        removeCount = 3;
      }
    }

    return {
      reference: {
        type: 'item',
        bomSlug: slugFromLinkText,
        identifier,
        optionalName
      },
      removeCount,
      remainderText
    };
  }

  const optionalName = decodeMarkdownLinkUrlValue(linkNode?.url);
  const bomTarget = normalizedPrefix === 'bom'
    ? parseBomReferenceTarget(slugFromLinkText)
    : { slug: slugFromLinkText, filterType: '', filterValue: '' };
  if (!bomTarget.slug) return null;

  return {
    reference: {
      type: normalizedPrefix,
      slug: bomTarget.slug,
      filterType: bomTarget.filterType,
      filterValue: bomTarget.filterValue,
      optionalName
    },
    removeCount: 2,
    remainderText: ''
  };
}

export function splitTextByBomReferences(rawValue) {
  const value = typeof rawValue === 'string' ? rawValue : '';
  if (!hasMarkdownReferencePrefix(value)) {
    return [{ type: 'text', value }];
  }

  const segments = [];
  let lastIndex = 0;

  value.replace(
    MARKDOWN_REFERENCE_TEXT_REGEX,
    (
      fullMatch,
      itemBomSlugRaw,
      itemIdentifierRaw,
      itemOptionalNameRaw,
      bomSlugRaw,
      bomOptionalNameRaw,
      guideSlugRaw,
      guideOptionalNameRaw,
      offset
    ) => {
      if (offset > lastIndex) {
        segments.push({ type: 'text', value: value.slice(lastIndex, offset) });
      }

      const reference = buildMarkdownReference([
        itemBomSlugRaw,
        itemIdentifierRaw,
        itemOptionalNameRaw,
        bomSlugRaw,
        bomOptionalNameRaw,
        guideSlugRaw,
        guideOptionalNameRaw
      ]);

      if (!reference) {
        segments.push({ type: 'text', value: fullMatch });
        lastIndex = offset + fullMatch.length;
        return fullMatch;
      }

      segments.push({
        type: 'reference',
        value: reference
      });

      lastIndex = offset + fullMatch.length;
      return fullMatch;
    }
  );

  if (lastIndex < value.length) {
    segments.push({ type: 'text', value: value.slice(lastIndex) });
  }

  if (!segments.length) {
    return [{ type: 'text', value }];
  }

  return segments;
}

export function buildMarkdownReferenceHref(referenceInput) {
  const type = normalizeReferenceType(referenceInput?.type);
  const optionalName = sanitizeReferencePart(referenceInput?.optionalName);

  let path = '';
  if (type === 'item') {
    const bomSlug = sanitizeReferencePart(referenceInput?.bomSlug);
    const identifier = sanitizeReferencePart(referenceInput?.identifier).toLowerCase();
    if (!bomSlug || !identifier) return '';
    path = `${MARKDOWN_REFERENCE_HREF_PREFIX}item/${encodeURIComponent(bomSlug)}/${encodeURIComponent(identifier)}`;
  } else if (type === 'bom') {
    const slug = sanitizeReferencePart(referenceInput?.slug);
    if (!slug) return '';
    path = `${MARKDOWN_REFERENCE_HREF_PREFIX}bom/${encodeURIComponent(slug)}`;
  } else if (type === 'guide') {
    const slug = sanitizeReferencePart(referenceInput?.slug);
    if (!slug) return '';
    path = `${MARKDOWN_REFERENCE_HREF_PREFIX}guide/${encodeURIComponent(slug)}`;
  } else {
    return '';
  }

  const searchParams = new URLSearchParams();
  if (type === 'bom') {
    const filterType = normalizeBomFilterType(referenceInput?.filterType);
    const filterValue = sanitizeReferencePart(referenceInput?.filterValue);
    if (filterType && filterValue) {
      searchParams.set('filterType', filterType);
      searchParams.set('filterValue', filterValue);
    }
  }
  if (optionalName) {
    searchParams.set('name', optionalName);
  }

  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
}

export function parseMarkdownReferenceHref(href) {
  const rawHref = (href || '').toString().trim();
  if (!rawHref) return null;

  let parsed;
  try {
    parsed = new URL(rawHref, 'https://stepwisedocs.local');
  } catch (e) {
    return null;
  }

  const pathname = parsed.pathname || '';
  if (!pathname.startsWith(MARKDOWN_REFERENCE_HREF_PREFIX)) {
    return null;
  }

  const rawParts = pathname.slice(MARKDOWN_REFERENCE_HREF_PREFIX.length).split('/').filter(Boolean);
  if (!rawParts.length) return null;

  const type = normalizeReferenceType(safeDecodeURIComponent(rawParts[0] || ''));
  const optionalName = sanitizeReferencePart(parsed.searchParams.get('name') || '');

  if (type === 'item') {
    if (rawParts.length < 3) return null;
    const bomSlug = sanitizeReferencePart(safeDecodeURIComponent(rawParts[1] || ''));
    const identifier = sanitizeReferencePart(safeDecodeURIComponent(rawParts[2] || '')).toLowerCase();
    if (!bomSlug || !identifier) return null;

    return {
      type,
      bomSlug,
      identifier,
      optionalName,
      key: `${type}|${normalizeReferenceKey(bomSlug)}|${normalizeReferenceKey(identifier)}`
    };
  }

  if (type === 'bom' || type === 'guide') {
    if (rawParts.length < 2) return null;
    const slug = sanitizeReferencePart(safeDecodeURIComponent(rawParts[1] || ''));
    if (!slug) return null;

    const reference = {
      type,
      slug,
      optionalName,
      key: `${type}|${normalizeReferenceKey(slug)}`
    };
    if (type === 'bom') {
      const filterType = normalizeBomFilterType(parsed.searchParams.get('filterType') || '');
      const filterValue = sanitizeReferencePart(parsed.searchParams.get('filterValue') || '');
      if (filterType && filterValue) {
        reference.filterType = filterType;
        reference.filterValue = filterValue;
        reference.key = `${reference.key}|${filterType}|${normalizeReferenceKey(filterValue)}`;
      }
    }
    return reference;
  }

  return null;
}

// Backward export name used by existing imports.
export const parseBomReferenceHref = parseMarkdownReferenceHref;

const SKIP_PARENT_TYPES = new Set(['link', 'linkReference', 'inlineCode', 'code', 'html']);

function shouldSkipTransform(parentType) {
  return SKIP_PARENT_TYPES.has(parentType);
}

export function bomReferenceRemarkPlugin() {
  return (tree) => {
    const walkNode = (node, parentType = '') => {
      if (!node || !Array.isArray(node.children) || !node.children.length) {
        return;
      }

      if (!shouldSkipTransform(parentType)) {
        for (let index = 0; index < node.children.length; index += 1) {
          const current = node.children[index];
          const next = node.children[index + 1];
          if (current?.type !== 'text' || next?.type !== 'link') continue;

          const currentValue = (current.value || '').toString();
          const markerInfo = findTrailingReferencePrefix(currentValue);
          const markerIndex = markerInfo.index;
          if (markerIndex < 0 || !markerInfo.prefix) continue;

          const afterMarker = currentValue.slice(markerIndex + markerInfo.prefix.length);
          if (afterMarker !== '') continue;

          const splitReference = buildMarkdownReferenceFromSplitNodes(
            markerInfo.prefix,
            next,
            node.children[index + 2]
          );
          if (!splitReference?.reference) continue;

          const beforeMarker = currentValue.slice(0, markerIndex);
          const replacementNodes = [];
          if (beforeMarker) {
            replacementNodes.push({
              type: 'text',
              value: beforeMarker
            });
          }

          replacementNodes.push(createMarkdownReferenceLinkNode(splitReference.reference));

          if (splitReference.remainderText) {
            replacementNodes.push({
              type: 'text',
              value: splitReference.remainderText
            });
          }

          node.children.splice(index, splitReference.removeCount || 2, ...replacementNodes);
          // Re-scan around the inserted nodes so any remainder text can pair with the
          // following parser-generated link for another same-line reference.
          index = Math.max(index - 1, -1);
        }
      }

      for (let index = 0; index < node.children.length; index += 1) {
        const child = node.children[index];

        if (child?.type === 'text' && !shouldSkipTransform(parentType)) {
          const segments = splitTextByBomReferences(child.value || '');
          const hasReferences = segments.some((segment) => segment.type === 'reference');

          if (hasReferences) {
            const replacementNodes = segments.map((segment) => {
              if (segment.type === 'text') {
                return {
                  type: 'text',
                  value: segment.value
                };
              }

              return createMarkdownReferenceLinkNode(segment.value);
            });

            node.children.splice(index, 1, ...replacementNodes);
            index += replacementNodes.length - 1;
            continue;
          }
        }

        walkNode(child, child?.type || '');
      }
    };

    walkNode(tree, tree?.type || '');
  };
}
